import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import HelpModal from './HelpModal';
import { getHelpContent, HelpContent } from './help-content';
import logger from '@/lib/logging';

interface HelpButtonProps {
  /**
   * Help content key to display
   * Must match a key in HELP_CONTENT
   */
  helpKey: string;
  
  /**
   * Button variant
   */
  variant?: 'default' | 'outline' | 'ghost' | 'link';
  
  /**
   * Button size
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';
  
  /**
   * Whether to show the text label (default: true on desktop)
   * When false, only icon is shown
   */
  showLabel?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Tooltip text (default: "View help for this screen")
   */
  tooltipText?: string;

  /**
   * Custom help content (overrides helpKey lookup)
   */
  customContent?: HelpContent;
}

/**
 * HelpButton Component
 * 
 * A reusable button that opens contextual help
 * Can be placed in headers, toolbars, or modals
 */
export const HelpButton: React.FC<HelpButtonProps> = ({
  helpKey,
  variant = 'outline',
  size = 'sm',
  showLabel = true,
  className = '',
  tooltipText = 'View help for this screen',
  customContent
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  
  // Get help content from key or use custom content
  const helpContent = customContent || getHelpContent(helpKey);
  
  // Don't render if no help content available
  if (!helpContent) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`HelpButton: No help content found for key "${helpKey}"`);
    }
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsHelpOpen(true);
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleClick}
              variant={variant}
              size={size}
              className={cn(
                className,
                !showLabel
                  ? 'h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10'
                  : 'h-8 w-8 sm:h-10 sm:w-auto sm:min-h-10',
              )}
              aria-label={tooltipText}
            >
              <HelpCircle className={`h-4 w-4 ${showLabel ? 'sm:mr-2' : ''}`} />
              {showLabel && <span className="hidden sm:inline">Help</span>}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        content={helpContent}
      />
    </>
  );
};

export default HelpButton;

